import { AnimatePresence, motion } from 'motion/react'
import { useState, useRef } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { cn } from 'src/shared/components/ui/utils'
import { isActive as checkIsActive } from '../../utils'

interface NavDropdownItem {
  label: string
  href: string
  description?: string
}

interface NavItemsProps {
  items: {
    label: string
    href: string
    dropdown?: {
      columns: {
        title: string
        items: NavDropdownItem[]
      }[]
    }
  }[]
  className?: string
  onItemClick?: (href: string) => void
  variant?: 'light' | 'dark'
}

export const NavItems = ({
  items,
  className,
  onItemClick,
  variant = 'light',
}: NavItemsProps) => {
  const [dropdownOpen, setDropdownOpen] = useState<number | null>(null)
  const location = useLocation()
  const isDark = variant === 'dark'
  const dropdownTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const isActive = (href: string) => checkIsActive(location.pathname, href)

  const handleMouseEnter = (idx: number, hasDropdown: boolean) => {
    if (dropdownTimeoutRef.current) clearTimeout(dropdownTimeoutRef.current)
    if (hasDropdown) setDropdownOpen(idx)
  }

  const handleMouseLeave = () => {
    dropdownTimeoutRef.current = setTimeout(() => {
      setDropdownOpen(null)
    }, 150)
  }

  const handleDropdownMouseEnter = () => {
    if (dropdownTimeoutRef.current) clearTimeout(dropdownTimeoutRef.current)
  }

  return (
    <div
      onMouseLeave={handleMouseLeave}
      className={cn(
        'absolute inset-0 hidden flex-1 flex-row items-center justify-center space-x-2 text-sm font-medium transition duration-200 lg:flex lg:space-x-2',
        isDark ? 'text-neutral-300 hover:text-white' : 'text-zinc-600 hover:text-zinc-800',
        className
      )}
    >
      {items.map((item, idx) => (
        <div
          key={`link-${idx}`}
          className="relative"
          onMouseEnter={() => handleMouseEnter(idx, !!item.dropdown)}
        >
          <div
            className={cn(
              'relative px-4 py-2.5 flex items-center gap-1 cursor-pointer rounded-full transition-colors duration-150',
              isDark
                ? isActive(item.href)
                  ? 'text-white bg-neutral-700'
                  : 'text-neutral-300 hover:bg-neutral-800 hover:text-white'
                : isActive(item.href)
                  ? 'text-neutral-800 bg-gray-100'
                  : 'text-neutral-600 hover:bg-gray-100 hover:text-neutral-800'
            )}
          >
            {item.dropdown ? (
              <span>{item.label}</span>
            ) : (
              <Link
                to={item.href}
                onClick={() => onItemClick?.(item.href)}
              >
                {item.label}
              </Link>
            )}
            {item.dropdown && (
              <svg
                className={cn('w-4 h-4 transition-transform duration-150', dropdownOpen === idx && 'rotate-180')}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            )}
          </div>

          <AnimatePresence>
            {item.dropdown && dropdownOpen === idx && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 10 }}
                transition={{ duration: 0.15 }}
                onMouseEnter={handleDropdownMouseEnter}
                className={cn(
                  'absolute top-full left-1/2 -translate-x-1/2 mt-2 rounded-xl shadow-lg border z-50',
                  isDark ? 'bg-neutral-900 border-neutral-700' : 'bg-white border-gray-200'
                )}
              >
                <div className="p-6 flex gap-10">
                  {item.dropdown.columns.map((column, colIdx) => (
                    <div key={colIdx} className="min-w-[180px]">
                      <p className={cn('text-xs font-semibold uppercase tracking-wider mb-4', isDark ? 'text-neutral-500' : 'text-gray-400')}>
                        {column.title}
                      </p>
                      <div className="flex flex-col gap-1">
                        {column.items.map((dropdownItem, itemIdx) => {
                          const hasLink = dropdownItem.href && dropdownItem.href !== ''
                          return (
                            <button
                              key={itemIdx}
                              onClick={() => {
                                if (hasLink) {
                                  onItemClick?.(dropdownItem.href)
                                  setDropdownOpen(null)
                                }
                              }}
                              className={cn(
                                'text-left px-3 py-2 rounded-lg transition-colors text-sm',
                                hasLink
                                  ? isDark
                                    ? 'text-neutral-300 hover:bg-neutral-800 hover:text-white cursor-pointer'
                                    : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900 cursor-pointer'
                                  : isDark
                                  ? 'text-neutral-500 cursor-default'
                                  : 'text-gray-400 cursor-default'
                              )}
                            >
                              {dropdownItem.label}
                            </button>
                          )
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      ))}
    </div>
  )
}
